import { Controller } from "@hotwired/stimulus"
import { rollDigits } from "../helpers/digit_roll"

// Spending pace strip (budgets/_spending_summary): one period at a time.
//
// The capsule in the section header picks the period. A pill slides inside it
// (measured the same way tabs_slider does), the pill's tail points at the
// strip, and the strip catches a soft spill of the same light under the
// chosen tab. Swiping the strip sideways changes the
// period too; tapping it opens that period's breakdown.
//
// The server renders every period's body. This shows one and animates the
// handover: the amount's digits drop in from above (digit_roll) and the bar
// regrows. The legend under the bar is not touched — it fits itself in CSS
// (.pace-legend, application.css), so the slices it names are the server's,
// the same set on every draw.
export default class extends Controller {
  static targets = ["tab", "pill", "ink", "spill", "strip", "link", "body", "sweep"]
  static values = { active: { type: String, default: "daily" } }

  // Movement under SLOP is a tap. Past it the gesture commits to horizontal or
  // hands over to the page (same rule swipe_actions uses). COMMIT is how far
  // the finger travels before the period changes.
  static SWIPE_SLOP_PX = 6
  static SWIPE_COMMIT_PX = 24
  // The click that trails a swipe would open the breakdown; ignore it.
  static CLICK_GUARD_MS = 300
  // The swipe hint plays once per app session, like the due-today rows.
  static HINT_KEY = "floosyo.spending-pace-swipe-hint.v1"
  static HINT_DELAY_MS = 900
  static HINT_DURATION_MS = 1400
  static SPILL_HALF_PX = 75
  static INK_WIDTH_PX = 24
  // How long the handover runs, end to end. The bar is the last thing still
  // moving: .pace-seg.is-grow is 700ms and the segments start 140ms apart at
  // 70ms each (budgets/_spending_summary sets the delay inline), so a five
  // segment bar finishes at 420 + 700. The capsule fades once that is done.
  static SETTLE_MS = 1120
  // A beat after the handover has finished before the tabs drop away, so the
  // last thing to land is not also the cue to leave.
  static SETTLE_HOLD_MS = 500

  connect() {
    this._reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this._onResize = () => this._refit()
    window.addEventListener("resize", this._onResize)
    if (typeof ResizeObserver !== "undefined") {
      this._resizeObserver = new ResizeObserver(this._onResize)
      this._resizeObserver.observe(this.element)
    }

    this._onPointerDown = this._pointerDown.bind(this)
    this._onPointerMove = this._pointerMove.bind(this)
    this._onPointerUp = this._pointerUp.bind(this)
    this._onClick = this._click.bind(this)
    const strip = this.stripTarget
    strip.addEventListener("pointerdown", this._onPointerDown)
    strip.addEventListener("pointermove", this._onPointerMove)
    strip.addEventListener("pointerup", this._onPointerUp)
    strip.addEventListener("pointercancel", this._onPointerUp)
    strip.addEventListener("click", this._onClick)

    // Deferred like tabs_slider: on Turbo cache restore the layout is not
    // flushed when connect() fires and the tab rects come back as zero.
    requestAnimationFrame(() => { this._fitTabs(); this._apply(false); this._maybeHint() })
    document.fonts?.ready?.then(() => this._refit())
  }

  disconnect() {
    clearTimeout(this._hintTimer)
    clearTimeout(this._settleTimer)
    window.removeEventListener("resize", this._onResize)
    this._resizeObserver?.disconnect()
    const strip = this.stripTarget
    strip.removeEventListener("pointerdown", this._onPointerDown)
    strip.removeEventListener("pointermove", this._onPointerMove)
    strip.removeEventListener("pointerup", this._onPointerUp)
    strip.removeEventListener("pointercancel", this._onPointerUp)
    strip.removeEventListener("click", this._onClick)
  }

  select(event) {
    const key = event.currentTarget.dataset.period
    if (key === this.activeValue) return
    this._go(key)
  }

  // ── period change ──────────────────────────────────────────────────

  _go(key) {
    const order = this._order()
    const dir = order.indexOf(key) > order.indexOf(this.activeValue) ? 1 : -1
    this.activeValue = key
    this._apply(true, dir)
  }

  _order() {
    return this.tabTargets.map(tab => tab.dataset.period)
  }

  // Full strength now, faded once the handover has finished. Called on every
  // apply, so a change that arrives mid-fade restarts the clock rather than
  // stacking a second timer. With reduced motion nothing is animating in the
  // first place, so there is nothing to wait out.
  _settle() {
    clearTimeout(this._settleTimer)
    this.element.classList.remove("is-settled")
    const handover = this._reducedMotion ? 0 : this.constructor.SETTLE_MS
    const wait = handover + this.constructor.SETTLE_HOLD_MS
    this._settleTimer = setTimeout(() => this.element.classList.add("is-settled"), wait)
  }

  _activeTab() {
    return this.tabTargets.find(tab => tab.dataset.period === this.activeValue)
  }

  _apply(animated, dir = 0) {
    this.tabTargets.forEach(tab => {
      const on = tab.dataset.period === this.activeValue
      tab.classList.toggle("is-active", on)
      tab.setAttribute("aria-selected", on ? "true" : "false")
    })
    this._place(animated)
    this._showBody(animated, dir)
    this._settle()

    const url = this._activeTab()?.dataset.url
    if (url && this.hasLinkTarget) this.linkTarget.href = url
  }

  _isRtl() {
    return getComputedStyle(this.element).direction === "rtl"
  }

  // Sizes change after connect: fonts land, the window turns, and a budget
  // panel that was hidden while this connected gets shown. Each of those
  // re-measures the tabs.
  _refit() {
    if (this.stripTarget.clientWidth === 0) return
    this._fitTabs()
    this._maybeHint()
  }

  // The capsule shares the title row. If a label cannot fit its tab, the
  // capsule goes compact; if that still clips, it takes a row of its own.
  _fitTabs() {
    if (!this.hasPillTarget) return
    const capsule = this.pillTarget.parentElement
    capsule.classList.remove("is-compact", "is-wrapped")
    const labels = this.tabTargets.map(tab => tab.querySelector(".pace-tab-label")).filter(Boolean)
    const clipped = () => labels.some(label => label.scrollWidth > label.clientWidth + 1)

    if (clipped()) capsule.classList.add("is-compact")
    if (clipped()) {
      capsule.classList.remove("is-compact")
      capsule.classList.add("is-wrapped")
    }
    this._place(false)
  }

  // The pill lives in the capsule, anchored at the inline-start edge of the
  // capsule's padding box, so its offset is measured from that edge (past the
  // border, negative under RTL). The ink and the spill live in the strip's
  // clip and are anchored at its left edge; both are placed from the pill's
  // own final position, so the ink's centre is the tail's centre in either
  // direction.
  _place(animated) {
    const tab = this._activeTab()
    if (!tab || !this.hasPillTarget) return

    const capsule = tab.parentElement
    const tabRect = tab.getBoundingClientRect()
    const capsuleRect = capsule.getBoundingClientRect()
    // Zero means the strip is hidden (a budget panel the carousel is not
    // showing) or not laid out yet. Measure once and stop: _refit places it
    // through the ResizeObserver once it has a size. Retrying every frame here
    // spun every hidden panel's strip at 60 calls a second.
    if (tabRect.width === 0 || capsuleRect.width === 0) return

    const transition = animated && !this._reducedMotion ? "" : "none"
    const rtl = this._isRtl()
    const border = capsule.clientLeft
    const pillOffset = rtl
      ? -((capsuleRect.right - border) - tabRect.right)
      : (tabRect.left - (capsuleRect.left + border))
    this._move(this.pillTarget, pillOffset, tabRect.width, transition)

    // Where the pill (and its tail) will settle, in screen coordinates.
    const pillCentre = rtl
      ? (capsuleRect.right - border) + pillOffset - tabRect.width / 2
      : (capsuleRect.left + border) + pillOffset + tabRect.width / 2
    const clipRect = (this.hasInkTarget ? this.inkTarget : this.spillTarget)?.parentElement.getBoundingClientRect()
    const clipLeft = clipRect ? clipRect.left : this.stripTarget.getBoundingClientRect().left
    const centre = pillCentre - clipLeft

    if (this.hasInkTarget) this._move(this.inkTarget, centre - this.constructor.INK_WIDTH_PX / 2, null, transition)
    if (this.hasSpillTarget) this._move(this.spillTarget, centre - this.constructor.SPILL_HALF_PX, null, transition)
  }

  _move(el, x, width, transition) {
    el.style.transition = transition
    if (width != null) el.style.width = `${width}px`
    el.style.transform = `translateX(${x}px)`
  }

  // ── body handover ──────────────────────────────────────────────────

  _showBody(animated, dir) {
    const animate = animated && !this._reducedMotion
    const forward = (dir > 0) !== this._isRtl()

    this.bodyTargets.forEach(body => {
      const on = body.dataset.period === this.activeValue
      body.classList.remove("pace-slide-right", "pace-slide-left")
      if (!on) {
        body.classList.add("hidden")
        return
      }
      if (animate && dir) body.classList.add(forward ? "pace-slide-right" : "pace-slide-left")
      body.classList.remove("hidden")
      this._roll(body, animate)
      this._regrow(body, animate)
    })

    if (animate) {
      const strip = this.stripTarget
      strip.classList.remove("pace-sheen")
      void strip.offsetWidth
      strip.classList.add("pace-sheen")
    }
  }

  // Digits drop into place from above, right-most first (digit_roll).
  _roll(body, animate) {
    const num = body.querySelector("[data-pace-num]")
    if (num) rollDigits(num, animate)
  }

  _regrow(body, animate) {
    body.querySelectorAll(".pace-seg").forEach(seg => {
      seg.classList.remove("is-grow")
      if (!animate) return
      void seg.offsetWidth
      seg.classList.add("is-grow")
    })
  }

  // ── swipe hint ─────────────────────────────────────────────────────
  // The first time the strip is on screen in a session: a light passes across
  // it in the swipe direction, the content drifts after it and settles, and
  // the chevron nods twice. Same session gate as swipe_actions' peek hint, so
  // returning users see it again, navigations within a session do not.

  _maybeHint() {
    if (this._hintQueued || this._reducedMotion) return
    if (this.stripTarget.clientWidth === 0) return
    if (this._hintSeen()) return
    this._hintQueued = true
    this._markHintSeen()
    this._hintTimer = setTimeout(() => this._playHint(), this.constructor.HINT_DELAY_MS)
  }

  _hintSeen() {
    try { return !!window.sessionStorage.getItem(this.constructor.HINT_KEY) } catch (_) { return false }
  }

  _markHintSeen() {
    try { window.sessionStorage.setItem(this.constructor.HINT_KEY, Date.now().toString()) } catch (_) { /* private mode */ }
  }

  _playHint() {
    if (typeof Element.prototype.animate !== "function") return
    // The swipe that reaches the next period travels toward the inline start.
    const dir = this._isRtl() ? -1 : 1
    const duration = this.constructor.HINT_DURATION_MS
    const quart = "cubic-bezier(0.25, 1, 0.5, 1)"
    const resist = "cubic-bezier(0.55, 0, 0.30, 1)"
    const body = this.bodyTargets.find(b => !b.classList.contains("hidden"))

    if (this.hasSweepTarget) {
      this.sweepTarget.animate([
        { transform: `translateX(${130 * dir}%)`, opacity: 0 },
        { transform: `translateX(${90 * dir}%)`, opacity: 1, offset: 0.15 },
        { transform: `translateX(${-130 * dir}%)`, opacity: 1, offset: 0.85 },
        { transform: `translateX(${-130 * dir}%)`, opacity: 0 }
      ], { duration, easing: quart, fill: "none" })
    }
    body?.animate([
      { offset: 0, transform: "translateX(0)", easing: resist },
      { offset: 0.45, transform: `translateX(${-10 * dir}px)`, easing: quart },
      { offset: 1, transform: "translateX(0)" }
    ], { duration, fill: "none", composite: "add" })
    body?.querySelector(".pace-cue svg")?.animate([
      { transform: "translateX(0)" },
      { transform: `translateX(${3 * dir}px)`, offset: 0.3 },
      { transform: "translateX(0)", offset: 0.5 },
      { transform: `translateX(${3 * dir}px)`, offset: 0.7 },
      { transform: "translateX(0)" }
    ], { duration, easing: "ease-in-out", fill: "none", composite: "add" })
  }

  // ── swipe ──────────────────────────────────────────────────────────

  _pointerDown(event) {
    this._startX = event.clientX
    this._startY = event.clientY
    this._committed = false
    this._swiped = false
  }

  _pointerMove(event) {
    if (this._startX == null || this._swiped) return
    const dx = event.clientX - this._startX
    const dy = event.clientY - this._startY

    if (!this._committed) {
      const { SWIPE_SLOP_PX } = this.constructor
      if (Math.abs(dx) < SWIPE_SLOP_PX && Math.abs(dy) < SWIPE_SLOP_PX) return
      // Mostly vertical: the page scrolls, this gesture is over.
      if (Math.abs(dy) > Math.abs(dx)) { this._startX = null; return }
      this._committed = true
    }
    if (Math.abs(dx) < this.constructor.SWIPE_COMMIT_PX) return

    this._swiped = true
    this._swipedAt = Date.now()
    let step = dx < 0 ? 1 : -1
    if (this._isRtl()) step = -step

    const order = this._order()
    const next = order[order.indexOf(this.activeValue) + step]
    if (!next) return
    this._go(next)
    this._tick()
  }

  _pointerUp() {
    this._startX = null
  }

  _click(event) {
    if (this._swipedAt && Date.now() - this._swipedAt < this.constructor.CLICK_GUARD_MS) {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  }

  // Same haptic the tab itself plays when tapped.
  _tick() {
    const tab = this._activeTab()
    if (!tab) return
    this.application.getControllerForElementAndIdentifier(tab, "bridge--haptic")?.tap()
  }
}
